import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import posthog from 'posthog-js';
import { fetchWebtokenHeadless } from './auth-helper';
import {
  paidAccessNotice,
  requestPaidAccess,
} from '@/containers/settings/billing/paid-access';

const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:8000';
const normalizedApiBase = apiBase.replace(/\/$/, '');
/** The API's `/api/v1` root, for a request RTK Query can't make (a
 * recording streamed as a file). */
export const baseApiUrl = normalizedApiBase.endsWith('/api/v1')
  ? normalizedApiBase
  : `${normalizedApiBase}/api/v1`;

const rawBaseQuery = fetchBaseQuery({
  baseUrl: baseApiUrl,
  prepareHeaders: async (headers) => {
    const token = await fetchWebtokenHeadless();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

const isMaintenance = (error: FetchBaseQueryError | undefined): boolean =>
  error?.status === 503 &&
  (error.data as { error?: { code?: string } } | undefined)?.error?.code ===
    'MAINTENANCE';

/**
 * Railway answers a few seconds of 502s while a deploy swaps instances, and
 * the browser mostly reports them as FETCH_ERROR: the edge's 502 carries no
 * CORS headers. A screen's own Retry often lands inside the same window, so
 * reads ride it out here. Only GETs: a write that failed in transit may still
 * have landed. Never a 4xx, nor the maintenance 503 (MaintenanceGate's).
 * RTK's `retry()` isn't used because it also retries thrown errors (a Clerk
 * token failure in prepareHeaders) five times for every method.
 */
const DEPLOY_RETRY_DELAYS_MS = [1000, 3000];

const isDeployBlip = (
  args: string | FetchArgs,
  error: FetchBaseQueryError | undefined
): boolean => {
  const method = typeof args === 'string' ? 'GET' : (args.method ?? 'GET');
  if (!error || method.toUpperCase() !== 'GET') return false;
  // A gateway's HTML error page that does reach us fails JSON parsing, so
  // judge it by the status it came with.
  const status =
    error.status === 'PARSING_ERROR' ? error.originalStatus : error.status;
  return (
    status === 'FETCH_ERROR' ||
    status === 502 ||
    (status === 503 && !isMaintenance(error))
  );
};

const baseQuery: typeof rawBaseQuery = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);
  for (const delay of DEPLOY_RETRY_DELAYS_MS) {
    if (api.signal.aborted || !isDeployBlip(args, result.error)) break;
    await new Promise((resolve) => setTimeout(resolve, delay));
    result = await rawBaseQuery(args, api, extraOptions);
  }
  return result;
};

const isActiveOrganizationConflict = (
  error: FetchBaseQueryError | undefined
): boolean => {
  if (error?.status !== 409 || typeof error.data !== 'object' || !error.data) {
    return false;
  }
  const envelope = (error.data as Record<string, unknown>).error;
  if (typeof envelope !== 'object' || !envelope) return false;
  const details = (envelope as Record<string, unknown>).details;
  return (
    typeof details === 'object' &&
    details !== null &&
    (details as Record<string, unknown>).reason ===
      'active_organization_changed'
  );
};

/**
 * Staff suspended the org (cleanse-api `_SUSPENDED_DETAIL`), so every call
 * 403s until they lift it. Each screen used to fail its own way (a plan check
 * with Retry, "Failed to load lead lists", a WebSocket reconnecting forever).
 * The first such 403 raises this event once, and the health check shows its
 * "Account restricted" dialog instead.
 * ponytail: matches the API's message; key on an error code once it sends one.
 */
export const ORGANIZATION_SUSPENDED_EVENT = 'cleanlist:organization-suspended';

let organizationSuspended = false;

/** The API has said this org is suspended. */
export const isOrganizationSuspended = (): boolean => organizationSuspended;

const isSuspendedOrganization = (
  error: FetchBaseQueryError | undefined
): boolean =>
  error?.status === 403 &&
  JSON.stringify(error.data ?? '').includes('organization has been suspended');

/**
 * Dev only: answers a request in the API's place, or returns null to send it.
 * The billing scenario simulator (src/dev/billing-simulator) registers one
 * from main.tsx. A production build never does, and every use of it sits
 * behind `import.meta.env.DEV`, which compiles to `false` there.
 */
export type RequestSimulator = (
  args: string | FetchArgs
) => Promise<{ data: unknown } | { error: FetchBaseQueryError } | null>;

let simulateRequest: RequestSimulator | null = null;

export function registerRequestSimulator(
  simulator: RequestSimulator | null
): void {
  if (!import.meta.env.DEV) return;
  simulateRequest = simulator;
}

export const baseQueryWithAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  if (import.meta.env.DEV && simulateRequest) {
    const simulated = await simulateRequest(args);
    if (simulated) return simulated;
  }

  let result = await baseQuery(args, api, extraOptions);

  // The API is in maintenance mode: re-read flags so MaintenanceGate takes
  // over mid-session instead of every screen erroring.
  if (isMaintenance(result.error)) {
    posthog.reloadFeatureFlags();
    return result;
  }

  // Handle authentication errors. Force a token refresh so we don't replay
  // the cached (and now-rejected) token - otherwise the retry 401s again
  // and the user sits in a stale-token loop until page reload.
  if (result.error && result.error.status === 401) {
    // Clerk can throw here (clerk_offline): keep the 401 rather than
    // escaping as an unhandled error.
    const newToken = await fetchWebtokenHeadless({ forceRefresh: true }).catch(
      () => null
    );
    if (newToken) {
      result = await baseQuery(args, api, extraOptions);
    }
  }

  // Clerk can expose the newly-active org before its cached JWT rotates. Retry
  // one guarded request with a fresh token. A truly stale org-A closure still
  // fails because its captured expected-org header remains A on the retry.
  const hasExpectedOrganization =
    typeof args !== 'string' &&
    (() => {
      const headers = args.headers;
      if (headers instanceof Headers) {
        return headers.has('X-Cleanlist-Expected-Org');
      }
      if (Array.isArray(headers)) {
        return headers.some(
          (entry) => entry[0]?.toLowerCase() === 'x-cleanlist-expected-org'
        );
      }
      return Object.keys(headers ?? {}).some(
        (name) => name.toLowerCase() === 'x-cleanlist-expected-org'
      );
    })();
  if (
    isActiveOrganizationConflict(result.error) &&
    hasExpectedOrganization &&
    (await fetchWebtokenHeadless({ forceRefresh: true }))
  ) {
    result = await baseQuery(args, api, extraOptions);
  }

  if (!organizationSuspended && isSuspendedOrganization(result.error)) {
    organizationSuspended = true;
    window.dispatchEvent(new CustomEvent(ORGANIZATION_SUSPENDED_EVENT));
  }

  // Only something the user did (a write) offers payment. A refused read is
  // background: the sidebar re-reads a failed agents list every minute, and
  // a Free org whose read was refused got the dialog back each time.
  const method = typeof args === 'string' ? 'GET' : (args.method ?? 'GET');
  const notice = paidAccessNotice(result.error);
  if (notice && method.toUpperCase() !== 'GET') requestPaidAccess(notice);
  if (result.error) {
    return {
      ...result,
      error: withRetryAfter(
        withLegacyDetail(result.error),
        result.meta?.response?.headers.get('Retry-After')
      ),
    };
  }
  return result;
};

/**
 * The API wraps every error as `{"error": {"code", "message", "details"}}`
 * (cleanse-api `error_envelope.py`), but most callers still read the shape
 * FastAPI sent before it: `data.detail`, a string or the raised dict. They
 * found nothing and showed a fallback ("Failed to start enrichment", "Failed
 * to start import") whatever the real reason was, a lead cap or a contact
 * already being processed.
 *
 * Put the envelope's message back under `detail` and `message`, as a string
 * only: plenty of callers toast `detail` as-is, so an object there would
 * render "[object Object]". Structured fields stay at `data.error.details`.
 * A message that only repeats the code ("BAD_REQUEST") is the envelope's
 * placeholder, not a reason, so it's left out and callers keep their
 * fallback.
 */
export function withLegacyDetail(
  error: FetchBaseQueryError
): FetchBaseQueryError {
  if (typeof error.data !== 'object' || error.data === null) return error;
  const data = error.data as Record<string, unknown>;
  if (data.detail !== undefined) return error;
  const envelope = data.error;
  if (typeof envelope !== 'object' || envelope === null) return error;
  const { code, message } = envelope as Record<string, unknown>;
  if (typeof message !== 'string' || !message.trim() || message === code) {
    return error;
  }
  return {
    ...error,
    data: { ...data, detail: message, message: data.message ?? message },
  };
}

/**
 * A 429's `Retry-After` (seconds) as `data.retry_after_seconds`: `.unwrap()`
 * hands callers only `{status, data}`, so a header would be out of reach for
 * the "available again at…" the hourly limits show.
 */
export function withRetryAfter(
  error: FetchBaseQueryError,
  retryAfter: string | null | undefined
): FetchBaseQueryError {
  const seconds = Number(retryAfter);
  if (
    error.status !== 429 ||
    !Number.isFinite(seconds) ||
    seconds <= 0 ||
    typeof error.data !== 'object' ||
    error.data === null
  ) {
    return error;
  }
  return {
    ...error,
    data: { ...(error.data as object), retry_after_seconds: seconds },
  };
}
